import { KeyboardEvent, KeyboardEventHandler, useCallback, useEffect, useState } from 'react';
import { MenuRow } from './menu-constants';

interface UseMenuKeyboardNavArgs<T> {
  rows: MenuRow<T>[];
  onSelect: (row: MenuRow<T>) => void;
  onEscape?: () => void;
}

interface UseMenuKeyboardNavResult {
  highlightedIndex: number;
  setHighlightedIndex: (i: number) => void;
  onKeyDown: KeyboardEventHandler;
}

// Owns the ↑/↓/Enter/Escape state for a Menu and exposes onKeyDown for the
// consumer to wire to whichever element actually holds focus (a text input
// for SearchCombobox, a popover container for DropdownMenu).
export const useMenuKeyboardNav = <T>({
  rows,
  onSelect,
  onEscape
}: UseMenuKeyboardNavArgs<T>): UseMenuKeyboardNavResult => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Reset the cursor whenever the rows list identity changes (e.g., new
  // fetch results in an autocomplete) so the highlight doesn't drift past
  // the end.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [rows]);

  const onKeyDown = useCallback<KeyboardEventHandler>(
    (e: KeyboardEvent) => {
      // Escape closes regardless of whether there are rows to highlight.
      if (e.key === 'Escape') {
        onEscape?.();
        return;
      }
      // Navigation keys only make sense once there are rows.
      if (rows.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(i => Math.min(i + 1, rows.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter': {
          const row = rows[highlightedIndex];
          if (row && !row.disabled) {
            e.preventDefault();
            onSelect(row);
          }
          break;
        }
        default:
          break;
      }
    },
    [rows, highlightedIndex, onSelect, onEscape]
  );

  return { highlightedIndex, setHighlightedIndex, onKeyDown };
};
